import { lastValueFrom } from 'rxjs';
import { SubscriptionStore2 } from '../observable/subscription.store2';
import { computed, inject, Injectable, InjectionToken } from '@angular/core';
import { SOFTLINE_SERVICE_UUID } from '../../../core.shared';
import { CancelledError } from '../../../types/errors';
import { NestedStore2Feature } from '../../nested-store2-feature';
import { LoadingState, SOFTLINE_LIST_REPOSITORY } from '../../store2.shared';
import { LoadListParameters } from '../../../repositories/abstraction';
import { ListResponseCommit, ResultListResponseCommit } from './strategies/list-response-commit';
import { ListStore2 } from './list.store2';

export interface ReadonlyRepositoryListState {
  loadingState: LoadingState;
  loadingError: Error | null;
}

export interface ListLoadActionParameters extends LoadListParameters {
  token?: string;
}

export const SOFTLINE_FACTORY_LIST_RESPONSE_COMMIT = new InjectionToken<() => ListResponseCommit<any>>('SOFTLINE_FACTORY_LIST_RESPONSE_COMMIT',
  { providedIn: 'root', factory: () => () => new ResultListResponseCommit()});

@Injectable()
export class ReadonlyRepositoryListStore2<T extends object, TState extends ReadonlyRepositoryListState = ReadonlyRepositoryListState> extends NestedStore2Feature<TState>{

  private readonly factory = inject(SOFTLINE_FACTORY_LIST_RESPONSE_COMMIT)
  protected readonly commitResponse =  this.factory();

  protected readonly store = inject(ListStore2<T>);
  protected readonly subscription = inject(SubscriptionStore2);
  protected readonly service = inject(SOFTLINE_LIST_REPOSITORY);
  protected readonly uuid = inject(SOFTLINE_SERVICE_UUID);

  loadingState = computed(() => this.state().loadingState);
  loadingError = computed(() => this.state().loadingError);

  constructor() {
    super();
  }

  async load(params: ListLoadActionParameters): Promise<T[]> {
    this.commitPatch({ loadingState: 'loading', loadingError: null } as Partial<TState>);
    const token = params.token ?? this.uuid();
    try {
      const value = await lastValueFrom(this.subscription.observe(
        token,
        this.service.load(params)
      ));
      this.commitPatch({ loadingState: 'loaded' } as Partial<TState>);
      this.commitResponse.set(value);
      return value;
    } catch (e) {
      if (e instanceof CancelledError)
        this.commitPatch({ loadingState: 'canceled' } as Partial<TState>);
      else
        this.commitPatch({ loadingState: 'failed', loadingError: e } as Partial<TState>);
      throw e;
    }
  }

  async loadOnce(params: ListLoadActionParameters): Promise<T[]> {
    const existing = this.store.state();
    if(existing)
      return existing;
    return await this.load(params);
  }

  async cancel(token: string): Promise<void> {
    this.subscription.cancel(token);
  }

  override getDefaultState(): TState {
    return {
      loadingState: null,
      loadingError: null,
    } as TState;
  }
}
